import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Contact from '../components/Contact';
import { ArrowRight, CheckCircle2, Thermometer, Wind, Zap, Home, TrendingDown, Shield, Phone, Settings, Wrench, Award, X, Leaf, Euro, Snowflake, Sun } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const wpTranslations = {
  de: {
    badge: 'Effiziente Heiztechnik',
    heroTitle: 'Moderne',
    heroTitle2: 'Wärmepumpen',
    heroDesc: 'Effiziente Heiz- und Kühlsysteme für maximalen Komfort und minimale Energiekosten. Mit bis zu 70% staatlicher Förderung in Ihre Zukunft investieren.',
    freeConsult: 'Kostenlose Beratung',
    learnMore: 'Mehr erfahren',
    subsidy: 'Förderung',
    efficiency: 'Effizienz',
    co2Free: 'CO₂-frei',
    types: 'Wärmepumpen-Typen',
    typesDesc: 'Die richtige Wärmepumpe für Ihr Zuhause',
    airWater: 'Luft-Wasser',
    airWaterDesc: 'Ideal für Bestandsgebäude und Neubauten. Nutzt die Außenluft als Wärmequelle.',
    brineWater: 'Sole-Wasser',
    brineWaterDesc: 'Höchste Effizienz durch Erdwärme. Ideal für Neubauten mit Garten.',
    waterWater: 'Wasser-Wasser',
    waterWaterDesc: 'Nutzt Grundwasser als Wärmequelle. Sehr effizient bei geeignetem Grundstück.',
    idealFor: 'Ideal für:',
    existing: 'Bestandsgebäude',
    newBuild: 'Neubauten',
    renovation: 'Sanierungen',
    garden: 'Grundstücke mit Garten',
    groundwater: 'Grundstücke mit Grundwasser',
    benefits: 'Ihre Vorteile',
    benefitsDesc: 'Warum eine Wärmepumpe die richtige Wahl ist',
    costSaving: 'Bis zu 70% Ersparnis',
    costSavingDesc: 'Reduzieren Sie Ihre Heizkosten drastisch',
    subsidyTitle: 'Staatliche Förderung',
    subsidyDesc: 'Bis zu 70% Förderung möglich',
    co2Neutral: 'CO₂-Neutral',
    co2Desc: 'Heizen mit 100% erneuerbarer Energie',
    cooling: 'Kühlung inklusive',
    coolingDesc: 'Im Sommer angenehm kühl',
    lowMaint: 'Wartungsarm',
    lowMaintDesc: 'Geringe laufende Kosten',
    longLife: 'Lange Lebensdauer',
    longLifeDesc: '20+ Jahre zuverlässiger Betrieb',
    premiumBrands: 'Premium Marken',
    premiumBrandsDesc: 'Wir arbeiten nur mit den besten Herstellern',
    ready: 'Bereit für Ihre neue Wärmepumpe?',
    readyDesc: 'Lassen Sie sich kostenlos beraten. Wir finden die perfekte Lösung für Ihr Zuhause.',
    requestConsult: 'Kostenlose Beratung anfragen',
    callUs: 'Oder rufen Sie uns an'
  },
  en: {
    badge: 'Efficient Heating Technology',
    heroTitle: 'Modern',
    heroTitle2: 'Heat Pumps',
    heroDesc: 'Efficient heating and cooling systems for maximum comfort and minimum energy costs. Invest in your future with up to 70% government subsidy.',
    freeConsult: 'Free Consultation',
    learnMore: 'Learn More',
    subsidy: 'Subsidy',
    efficiency: 'Efficiency',
    co2Free: 'CO₂-free',
    types: 'Heat Pump Types',
    typesDesc: 'The right heat pump for your home',
    airWater: 'Air-Water',
    airWaterDesc: 'Ideal for existing buildings and new construction. Uses outdoor air as heat source.',
    brineWater: 'Brine-Water',
    brineWaterDesc: 'Highest efficiency through geothermal energy. Ideal for new builds with garden.',
    waterWater: 'Water-Water',
    waterWaterDesc: 'Uses groundwater as heat source. Very efficient with suitable property.',
    idealFor: 'Ideal for:',
    existing: 'Existing Buildings',
    newBuild: 'New Construction',
    renovation: 'Renovations',
    garden: 'Properties with Garden',
    groundwater: 'Properties with Groundwater',
    benefits: 'Your Benefits',
    benefitsDesc: 'Why a heat pump is the right choice',
    costSaving: 'Up to 70% Savings',
    costSavingDesc: 'Drastically reduce your heating costs',
    subsidyTitle: 'Government Subsidy',
    subsidyDesc: 'Up to 70% subsidy possible',
    co2Neutral: 'CO₂-Neutral',
    co2Desc: 'Heating with 100% renewable energy',
    cooling: 'Cooling Included',
    coolingDesc: 'Pleasantly cool in summer',
    lowMaint: 'Low Maintenance',
    lowMaintDesc: 'Low running costs',
    longLife: 'Long Lifespan',
    longLifeDesc: '20+ years of reliable operation',
    premiumBrands: 'Premium Brands',
    premiumBrandsDesc: 'We only work with the best manufacturers',
    ready: 'Ready for Your New Heat Pump?',
    readyDesc: 'Get free advice. We will find the perfect solution for your home.',
    requestConsult: 'Request Free Consultation',
    callUs: 'Or call us'
  }
};

const WaermepumpeHero = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = wpTranslations[language] || wpTranslations.de;

  return (
    <section className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/o0q5bx1y_Luft%20Wasser%20warmepumpe%20moderna.jpeg"
          alt="Moderne Wärmepumpe"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-sky-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Thermometer className="w-5 h-5 text-sky-400" />
              <span className="text-sky-300 text-sm font-medium">{t.badge}</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {t.heroTitle}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-amber-400">
                {t.heroTitle2}
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed max-w-2xl">
              {t.heroDesc}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button 
                onClick={() => navigate('/beratung')}
                size="lg"
                className="bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white px-8 py-6 text-lg group shadow-xl shadow-sky-500/25"
              >
                {t.freeConsult}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => document.getElementById('vorteile').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-white/80 text-white hover:bg-white/10 backdrop-blur-sm px-8 py-6 text-lg"
              >
                {t.learnMore}
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-white/20">
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">70%</div>
                <div className="text-sm text-gray-300">{t.subsidy}</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">400%</div>
                <div className="text-sm text-gray-300">{t.efficiency}</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">100%</div>
                <div className="text-sm text-gray-300">{t.co2Free}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/70 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

// How Heat Pump Works Section
const WieFunktionierts = () => {
  const { language } = useLanguage();
  const steps = language === 'en' ? [
    { number: '01', title: 'Absorb Heat', description: 'The heat pump extracts thermal energy from the environment (air, ground or water).', icon: Wind },
    { number: '02', title: 'Compress', description: 'A compressor compresses the refrigerant, increasing the temperature.', icon: Zap },
    { number: '03', title: 'Release Heat', description: 'The heat is released into your heating system.', icon: Home },
    { number: '04', title: 'Cooling', description: 'The refrigerant cools down and the cycle begins again.', icon: Snowflake }
  ] : [
    { number: '01', title: 'Wärme aufnehmen', description: 'Die Wärmepumpe entzieht der Umgebung (Luft, Erde oder Wasser) Wärmeenergie.', icon: Wind },
    { number: '02', title: 'Verdichten', description: 'Ein Kompressor verdichtet das Kältemittel und erhöht dabei die Temperatur.', icon: Zap },
    { number: '03', title: 'Wärme abgeben', description: 'Die Wärme wird an Ihr Heizsystem abgegeben.', icon: Home },
    { number: '04', title: 'Abkühlen', description: 'Das Kältemittel kühlt ab und der Kreislauf beginnt von vorne.', icon: Snowflake }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-sky-900 to-slate-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {language === 'en' ? 'How it ' : 'So '}<span className="text-amber-400">{language === 'en' ? 'Works' : "funktioniert's"}</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {language === 'en' ? 'Simply explained: The heat pump turns environmental energy into cozy warmth' : 'Einfach erklärt: Die Wärmepumpe macht aus Umweltenergie wohlige Wärme'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={index} className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 h-full border border-white/20 hover:bg-white/15 transition-colors">
                  <div className="text-6xl font-bold text-white/20 mb-4">{step.number}</div>
                  <div className="w-12 h-12 bg-sky-500 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-gray-400">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-sky-500/50" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const WaermepumpeVorteile = () => {
  const { language } = useLanguage();
  const vorteile = language === 'en' ? [
    { icon: TrendingDown, title: 'Low Operating Costs', description: 'Up to 75% less heating costs compared to gas or oil heating', color: 'from-green-400 to-green-600' },
    { icon: Wind, title: 'Heating & Cooling', description: 'Year-round comfort with heating in winter and cooling in summer', color: 'from-sky-400 to-sky-600' },
    { icon: Zap, title: 'High Efficiency', description: 'From 1 kWh of electricity, up to 5 kWh of thermal energy is generated', color: 'from-amber-400 to-amber-600' },
    { icon: Home, title: 'Value Increase', description: 'Increase the value of your property with modern heating technology', color: 'from-purple-400 to-purple-600' },
    { icon: Leaf, title: 'Environmentally Friendly', description: 'CO₂-neutral heating with renewable energy', color: 'from-emerald-400 to-emerald-600' },
    { icon: Euro, title: 'Up to 70% Subsidy', description: 'Benefit from attractive government funding programs', color: 'from-rose-400 to-rose-600' }
  ] : [
    { icon: TrendingDown, title: 'Niedrige Betriebskosten', description: 'Bis zu 75% weniger Heizkosten im Vergleich zu Gas- oder Ölheizungen', color: 'from-green-400 to-green-600' },
    { icon: Wind, title: 'Heizen & Kühlen', description: 'Ganzjähriger Komfort durch Heizen im Winter und Kühlen im Sommer', color: 'from-sky-400 to-sky-600' },
    { icon: Zap, title: 'Hohe Effizienz', description: 'Aus 1 kWh Strom werden bis zu 5 kWh Wärmeenergie erzeugt', color: 'from-amber-400 to-amber-600' },
    { icon: Home, title: 'Wertsteigerung', description: 'Erhöhen Sie den Wert Ihrer Immobilie durch moderne Heiztechnik', color: 'from-purple-400 to-purple-600' },
    { icon: Leaf, title: 'Umweltfreundlich', description: 'CO₂-neutrales Heizen mit erneuerbarer Energie', color: 'from-emerald-400 to-emerald-600' },
    { icon: Euro, title: 'Bis zu 70% Förderung', description: 'Profitieren Sie von attraktiven staatlichen Förderprogrammen', color: 'from-rose-400 to-rose-600' }
  ];

  return (
    <section id="vorteile" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Your ' : 'Ihre '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'Benefits' : 'Vorteile'}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' ? 'Why a heat pump is the best choice for your home' : 'Warum eine Wärmepumpe die beste Wahl für Ihr Zuhause ist'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {vorteile.map((vorteil, index) => {
            const Icon = vorteil.icon;
            return (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-0 bg-white hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className={`w-16 h-16 bg-gradient-to-br ${vorteil.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{vorteil.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{vorteil.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Photo Gallery
const WaermepumpeGalerie = () => {
  const { language } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  
  const photos = language === 'en' ? [
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/os63hyzf_IMG_7530.jpeg', title: 'Vaillant Heat Pump', category: 'Our Project' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/qgdq8rob_IMG_7294.jpeg', title: 'Bosch Heat Pump', category: 'Our Project' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/gbv5mjcl_IMG_0093.jpeg', title: 'Garden Installation', category: 'Our Project' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/y2w2dy33_IMG_0089.png', title: 'Air-Water Premium', category: 'Installation' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/x36g83oh_IMG_0088.png', title: 'Design Heat Pump', category: 'Premium' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/rthcvf3a_IMG_0087.png', title: 'Dual Fan System', category: 'Industry' }
  ] : [
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/os63hyzf_IMG_7530.jpeg', title: 'Vaillant Wärmepumpe', category: 'Unser Projekt' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/qgdq8rob_IMG_7294.jpeg', title: 'Bosch Wärmepumpe', category: 'Unser Projekt' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/gbv5mjcl_IMG_0093.jpeg', title: 'Garten Installation', category: 'Unser Projekt' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/y2w2dy33_IMG_0089.png', title: 'Luft-Wasser Premium', category: 'Installation' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/x36g83oh_IMG_0088.png', title: 'Design Wärmepumpe', category: 'Premium' },
    { src: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/rthcvf3a_IMG_0087.png', title: 'Doppelventilator Anlage', category: 'Industrie' }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Our ' : 'Unsere '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'References' : 'Referenzen'}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' ? 'Successful heat pump installations from our customers' : 'Erfolgreiche Wärmepumpen-Installationen unserer Kunden'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {photos.map((photo, index) => (
            <div 
              key={index} 
              className="group cursor-pointer relative overflow-hidden rounded-xl shadow-lg"
              onClick={() => setSelectedImage(photo)}
            >
              <div className="aspect-[4/3]">

                {photo.type === 'video' ? (

                  <video

                    src={photo.src}

                    aria-label={photo.title}

                    className="w-full h-full object-cover"

                    controls

                    playsInline

                    loop

                    preload="metadata"

                    onClick={(event) => event.stopPropagation()}

                  />

                ) : (

                  <img

                    src={photo.src}

                    alt={photo.title}

                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"

                  />

                )}

              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-sky-400 text-sm font-medium">{photo.category}</span>
                  <h3 className="text-white text-xl font-bold">{photo.title}</h3>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button className="absolute top-4 right-4 text-white hover:text-gray-300">
              <X className="w-8 h-8" />
            </button>
            <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
              {selectedImage.type === 'video' ? (

                <video

                  src={selectedImage.src}

                  aria-label={selectedImage.title}

                  className="w-full h-auto rounded-xl bg-black"

                  controls

                  playsInline

                  loop

                  preload="metadata"

                  autoPlay

                />

              ) : (

                <img

                  src={selectedImage.src}

                  alt={selectedImage.title}

                  className="w-full h-auto rounded-xl"

                />

              )}
              <div className="text-center mt-4">
                <span className="text-sky-400 text-sm font-medium">{selectedImage.category}</span>
                <h3 className="text-white text-2xl font-bold">{selectedImage.title}</h3>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const WaermepumpeTypen = () => {
  const { language } = useLanguage();
  
  const typen = language === 'en' ? [
    {
      name: 'Air-Water Heat Pump',
      image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/rw9fr8lc_IMG_0094.jpeg',
      beschreibung: 'The most popular heat pump uses outdoor air as an energy source',
      features: [
        'Easy installation',
        'No permit required',
        'For new builds and renovations',
        'Low investment costs'
      ],
      price: 'from €12,000',
      priceNote: 'before subsidy'
    },
    {
      name: 'Brine-Water Heat Pump',
      image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/ofve1uml_IMG_0090.jpeg',
      beschreibung: 'Uses constant geothermal energy for highest efficiency',
      features: [
        'Highest efficiency (COP up to 5.0)',
        'Silent operation',
        'Durable and low maintenance',
        'Ideal for new builds'
      ],
      price: 'from €18,000',
      priceNote: 'before subsidy'
    },
    {
      name: 'Water-Water Heat Pump',
      image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/z8vz7ttj_IMG_0092.jpeg',
      beschreibung: 'Uses groundwater as a constant heat source',
      features: [
        'Very high efficiency',
        'Year-round consistent performance',
        'Low operating costs',
        'With sufficient groundwater'
      ],
      price: 'from €20,000',
      priceNote: 'before subsidy'
    }
  ] : [
    {
      name: 'Luft-Wasser-Wärmepumpe',
      image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/rw9fr8lc_IMG_0094.jpeg',
      beschreibung: 'Die beliebteste Wärmepumpe nutzt die Außenluft als Energiequelle',
      features: [
        'Einfache Installation',
        'Keine Genehmigung erforderlich',
        'Für Neubau und Sanierung',
        'Geringe Investitionskosten'
      ],
      price: 'ab 12.000 €',
      priceNote: 'vor Förderung'
    },
    {
      name: 'Sole-Wasser-Wärmepumpe',
      image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/ofve1uml_IMG_0090.jpeg',
      beschreibung: 'Nutzt die konstante Erdwärme für höchste Effizienz',
      features: [
        'Höchste Effizienz (COP bis 5,0)',
        'Geräuschloser Betrieb',
        'Langlebig und wartungsarm',
        'Ideal für Neubau'
      ],
      price: 'ab 18.000 €',
      priceNote: 'vor Förderung'
    },
    {
      name: 'Wasser-Wasser-Wärmepumpe',
      image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/z8vz7ttj_IMG_0092.jpeg',
      beschreibung: 'Nutzt Grundwasser als konstante Wärmequelle',
      features: [
        'Sehr hohe Effizienz',
        'Ganzjährig konstante Leistung',
        'Niedrige Betriebskosten',
        'Bei ausreichend Grundwasser'
      ],
      price: 'ab 20.000 €',
      priceNote: 'vor Förderung'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Heat Pump ' : 'Wärmepumpen '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'Types' : 'Typen'}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' ? 'Find the perfect heat pump for your home' : 'Finden Sie die perfekte Wärmepumpe für Ihr Zuhause'}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {typen.map((typ, index) => (
            <Card key={index} className="border-0 shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="h-48 overflow-hidden">
                <img 
                  src={typ.image}
                  alt={typ.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{typ.name}</h3>
                <p className="text-gray-600 mb-4 text-sm">{typ.beschreibung}</p>
                <ul className="space-y-2 mb-6">
                  {typ.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-gray-700 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-sky-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-2xl font-bold text-sky-600">{typ.price}</span>
                  <span className="text-xs text-gray-500">{typ.priceNote}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Comparison Section: Heating vs Heat Pump
const Vergleich = () => {
  const { language } = useLanguage();
  
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Heating Costs ' : 'Heizkosten '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'Comparison' : 'Vergleich'}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' ? 'See how much you can save with a heat pump' : 'Sehen Sie, wie viel Sie mit einer Wärmepumpe sparen können'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Old Heating */}
          <Card className="border-2 border-red-200 bg-red-50">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                  <Thermometer className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{language === 'en' ? 'Gas/Oil Heating' : 'Gas-/Ölheizung'}</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{language === 'en' ? 'Annual Heating Costs' : 'Jährliche Heizkosten'}</span>
                  <span className="text-2xl font-bold text-red-600">~3.500 €</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{language === 'en' ? 'CO₂ Emissions/Year' : 'CO₂-Ausstoß/Jahr'}</span>
                  <span className="font-bold text-red-600">~6.000 kg</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{language === 'en' ? 'Price Trend' : 'Preisentwicklung'}</span>
                  <span className="font-bold text-red-600">↑ {language === 'en' ? 'Rising' : 'Steigend'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Heat Pump */}
          <Card className="border-2 border-green-200 bg-green-50">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                  <Wind className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{language === 'en' ? 'Heat Pump' : 'Wärmepumpe'}</h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{language === 'en' ? 'Annual Heating Costs' : 'Jährliche Heizkosten'}</span>
                  <span className="text-2xl font-bold text-green-600">~900 €</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{language === 'en' ? 'CO₂ Emissions/Year' : 'CO₂-Ausstoß/Jahr'}</span>
                  <span className="font-bold text-green-600">~0 kg*</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{language === 'en' ? 'Price Trend' : 'Preisentwicklung'}</span>
                  <span className="font-bold text-green-600">→ {language === 'en' ? 'Stable' : 'Stabil'}</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">{language === 'en' ? '*with green electricity or own PV system' : '*mit Ökostrom oder eigener PV-Anlage'}</p>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-2 bg-green-100 px-6 py-3 rounded-full">
            <Euro className="w-6 h-6 text-green-600" />
            <span className="text-lg font-bold text-green-700">{language === 'en' ? 'You save up to €2,600 per year!' : 'Sie sparen bis zu 2.600 € pro Jahr!'}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const Foerderung = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  return (
    <section className="py-20 bg-gradient-to-br from-sky-600 to-amber-500">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center text-white">
          <Award className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            {language === 'en' ? 'Secure up to 70% Subsidy!' : 'Bis zu 70% Förderung sichern!'}
          </h2>
          <p className="text-xl mb-8 leading-relaxed opacity-90">
            {language === 'en' 
              ? 'The government generously subsidizes the switch to heat pumps. We support you in applying for all available funding.'
              : 'Der Staat fördert den Umstieg auf Wärmepumpen großzügig. Wir unterstützen Sie bei der Beantragung aller verfügbaren Fördermittel.'}
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-4xl font-bold mb-2">30%</div>
              <div className="text-sm">{language === 'en' ? 'Base Subsidy' : 'Basis-Förderung'}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-4xl font-bold mb-2">+20%</div>
              <div className="text-sm">{language === 'en' ? 'Climate Speed Bonus' : 'Klima-Geschwindigkeits-Bonus'}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-4xl font-bold mb-2">+20%</div>
              <div className="text-sm">{language === 'en' ? 'Income Bonus' : 'Einkommens-Bonus'}</div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/beratung')}
              size="lg"
              className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg font-bold shadow-xl"
            >
              {language === 'en' ? 'Calculate Subsidy Now' : 'Jetzt Förderung berechnen'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a 
              href="tel:+4915782283963"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg text-lg font-medium transition-colors"
            >
              <Phone className="w-5 h-5" />
              +49 157 82283963
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Process Section
const InstallationsProzess = () => {
  const { language } = useLanguage();
  
  const steps = language === 'en' ? [
    {
      title: 'Consultation & Planning',
      description: 'Free initial consultation, needs analysis and individual planning',
      duration: '1-2 weeks'
    },
    {
      title: 'Subsidy Application',
      description: 'We handle the application for all available funding',
      duration: '2-4 weeks'
    },
    {
      title: 'Installation',
      description: 'Professional installation by our certified technicians',
      duration: '2-3 days'
    },
    {
      title: 'Commissioning',
      description: 'Setup, instruction and handover of the system',
      duration: '1 day'
    }
  ] : [
    {
      title: 'Beratung & Planung',
      description: 'Kostenlose Erstberatung, Bedarfsanalyse und individuelle Planung',
      duration: '1-2 Wochen'
    },
    {
      title: 'Förderantrag',
      description: 'Wir übernehmen die Beantragung aller verfügbaren Fördermittel',
      duration: '2-4 Wochen'
    },
    {
      title: 'Installation',
      description: 'Professionelle Montage durch unsere zertifizierten Techniker',
      duration: '2-3 Tage'
    },
    {
      title: 'Inbetriebnahme',
      description: 'Einstellung, Einweisung und Übergabe der Anlage',
      duration: '1 Tag'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'The Path to Your ' : 'Der Weg zu Ihrer '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{language === 'en' ? 'Heat Pump' : 'Wärmepumpe'}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'en' ? "From consultation to finished system - it's that easy" : "Von der Beratung bis zur fertigen Anlage - so einfach geht's"}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {steps.map((step, index) => (
            <div key={index} className="flex gap-6 mb-8 last:mb-0">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-sky-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div className="w-0.5 h-full bg-sky-200 mt-2"></div>
                )}
              </div>
              <div className="flex-1 pb-8">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                  <span className="text-sm text-sky-600 bg-sky-100 px-3 py-1 rounded-full">{step.duration}</span>
                </div>
                <p className="text-gray-600">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WaermepumpePage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <WaermepumpeHero />
      <WieFunktionierts />
      <WaermepumpeVorteile />
      <WaermepumpeGalerie />
      <WaermepumpeTypen />
      <Vergleich />
      <InstallationsProzess />
      <Foerderung />
      <Contact />
      <Footer />
    </div>
  );
};

export default WaermepumpePage;
